// Per-CLIENT static overrides. Keyed by the CLIENT env value (set at build time
// via craco — see craco.config.js DefinePlugin).
//
// Add an entry here when a client needs to diverge from main src behaviour
// (SSO type, API URLs, per-client feature flags). Anything not listed falls
// back to the `default` profile.

const CLIENT = process.env.CLIENT || 'web';

const clientConfigs = {
  default: {
    ssoType: 'visit',
    apiBaseUrl: {
      staging: 'https://stage-edit-bank-details.visithealth.co/backend',
      development: 'https://stage-edit-bank-details.visithealth.co/backend',
    },
    pharmaServer: {
      development: 'https://api.getvisitapp.net/pharmacy',
      staging: 'https://api.getvisitapp.net/pharmacy',
    },
    useDopeRelatives: false,
    showSponsorLogoInWebVersion: false,
    showUpgradePackagesSection: true,
    forceInAppCashlessCartRedirect: false,
    // Brand theme. Applied to CSS variables at startup (see utils/applyTheme).
    // Client entries may override any subset — missing keys fall back to these.
    theme: {
      colors: {
        primary: '#714fff',
        secondary: '#0049c6',
        tertiary: '#ff754c',
      },
      fonts: {
        primary: "'Inter', sans-serif",
        secondary: "'Montserrat', sans-serif",
      },
    },
  },
  mibl: {
    ssoType: 'universal',
    apiBaseUrl: {
      staging: 'https://insurance-uat.getvisitapp.net/loki',
      development: 'https://api.samuraijack.xyz/latios',
    },
    pharmaServer: {
      staging: 'https://api.getvisitapp.net/agni',
    },
    useDopeRelatives: true,
    showUpgradePackagesSection: false,
    isWebView: false,
    forceInAppCashlessCartRedirect: false,
  },
  // abw — migration in progress from the src-abw fork to unified src.
  // Config/theme registered here; build still points at src-abw in projectconfig
  // until the fork's SSO flow and custom flows are reconciled into src.
  abw: {
    ssoType: 'abw',
    apiBaseUrl: {
      staging: 'https://insurance-uat.getvisitapp.net/slayer',
      development: 'https://insurance-uat.getvisitapp.net/slayer',
    },
    pharmaServer: {
      staging: 'https://api.getvisitapp.net/pharmacy',
    },
    // Fork always fetches relatives via the dope endpoint (care/dope/get-relatives).
    useDopeRelatives: true,
    showUpgradePackagesSection: false,
    forceInAppCashlessCartRedirect: false,

    theme: {
      colors: {
        // abw brand red + light tints (from src-abw/App.css).
        // secondary/tertiary + fonts inherit the default profile.
        primary: '#c7222a',
        shades1: '#f5d7d9',
        light: '#f9e9ea',
      },
      fonts: {
        // abw brand font (fork body uses Anek Latin). secondary inherits default.
        primary: "'Anek Latin', serif",
      },
    },
    showSponsorLogoInWebVersion: false,
    isWebView: false,
  },
  sencov: {
    ssoType: 'universal',
    apiBaseUrl: {
      staging: 'https://api.samuraijack.xyz/latios',
      development: 'https://api.samuraijack.xyz/latios',
    },
    pharmaServer: {
      staging: 'https://api.getvisitapp.net/agni',
    },
    myntraSponsorIds: [18860],
    showForYouSection: [14, 77],
    showUpgradePackagesSection: false,
    useDopeRelatives: true,
    showSponsorLogoInWebVersion: false,
    isWebView: false,
    labUploadRxCorporateName: 'MedoEase',
    forceInAppCashlessCartRedirect: false,
  },
  coversure: {
    ssoType: 'retail',
    apiBaseUrl: {
      staging: 'https://api.samuraijack.xyz/latios',
      development: 'https://api.samuraijack.xyz/latios',
    },
    pharmaServer: {
      staging: 'https://api.getvisitapp.net/agni',
    },
    myntraSponsorIds: [18860],
    showForYouSection: [14, 77],
    useDopeRelatives: true,
    showSponsorLogoInWebVersion: false,
    isWebView: false,
    forceInAppCashlessCartRedirect: false,
  },
  wyh: {
    ssoType: 'universal',
    apiBaseUrl: {
      staging: 'https://stage-stage-digvijay.visithealth.co/backend',
      development: 'https://insurance-uat.getvisitapp.net/slayer',
    },
    pharmaServer: {
      staging: 'https://api.getvisitapp.net/pharmacy',
    },
    // Fork always fetches relatives via the dope endpoint (care/dope/get-relatives).
    useDopeRelatives: true,
    showUpgradePackagesSection: false,
    theme: {
      colors: {
        primary: '#243A6F',
        shades1: '#F9A51C',
        light: '#E9EBF1',
      },
    },
    showSponsorLogoInWebVersion: true,
    isWebView: true,
    forceInAppCashlessCartRedirect: false,
  },
  'wyh-vendor': {
    ssoType: 'universal',
    apiBaseUrl: {
      staging: 'https://stage-stage-digvijay.visithealth.co/backend',
      development: 'https://insurance-uat.getvisitapp.net/slayer',
    },
    pharmaServer: {
      staging: 'https://api.getvisitapp.net/pharmacy',
    },
    // Fork always fetches relatives via the dope endpoint (care/dope/get-relatives).
    useDopeRelatives: true,
    showUpgradePackagesSection: false,
    theme: {
      colors: {
        primary: '#ED1C24',
        shades1: '#FACBCD',
        light: '#FFF5F5',
      },
    },
    showSponsorLogoInWebVersion: true,
    isWebView: true,
    forceInAppCashlessCartRedirect: false,
  },

  // across-assist — modelled on the wyh profile (unified src, no fork).
  // TODO(across-assist): theme.colors.primary / shades1 / light — brand hexes
  // still pending. Until set the app renders the default Visit purple (#714fff).
  'across-assist': {
    ssoType: 'universal',
    apiBaseUrl: {
      staging: 'https://insurance-uat.getvisitapp.net/tansen',
      development: 'https://insurance-uat.getvisitapp.net/tansen',
    },
    pharmaServer: {
      staging: 'https://api.getvisitapp.net/pharmacy',
    },
    // Relatives come from the dope endpoint (care/dope/get-relatives), which
    // needs policyNumber/wellnessId in the SSO response.
    useDopeRelatives: true,
    showUpgradePackagesSection: false,
    // Loaded inside the client's native WebView, not an iframe — so "back" must
    // fall through to the native close handlers (Android.closeView /
    // ReactNativeWebView / visitIosView) in dismissNativeWebViewOrNavigateHome.
    // Setting this true would post BACK_TO_HOME to window.parent (=== window
    // here, nothing listens) and dead-end the back button.
    isWebView: false,
    showSponsorLogoInWebVersion: true,
    forceInAppCashlessCartRedirect: false,
  },

  // nhance — migrated from the src-nhance fork. Keeps the default Visit purple.
  nhance: {
    ssoType: 'retail',
    apiBaseUrl: {
      staging: 'https://api.getvisitapp.net/stoker',
      development: 'https://api.getvisitapp.net/stoker',
    },
    pharmaServer: {
      development: 'https://retail-stage.getvisitapp.net/ultron',
      staging: 'https://retail-stage.getvisitapp.net/ultron',
    },
    useDopeRelatives: true,
    // nhance's SSO response carries the dope policy identifier in `employeeId`.
    ssoPolicyNumberKey: 'employeeId',
    // SSO session is scoped to one member: pre-select that relative instead of
    // asking the user to choose a patient.
    autoSelectSsoRelative: true,
    showSponsorLogoInWebVersion: false,
    showUpgradePackagesSection: false,
    forceInAppCashlessCartRedirect: false,
  },

  'tatva-sdk': {
    ssoType: 'universal',
    apiBaseUrl: {
      staging: 'https://insurance-uat.getvisitapp.net/asteroid',
      development: 'https://stage-coupons-test.visithealth.co/backend',
    },
    pharmaServer: {
      development: 'https://api.getvisitapp.net/pharmacy',
      staging: 'https://api.getvisitapp.net/pharmacy',
    },
    useDopeRelatives: true,
    showSponsorLogoInWebVersion: true,
    showUpgradePackagesSection: false,
    showMyOrderInAhc: true,
    forceInAppCashlessCartRedirect: true,
  },
  'pension-bazaar': {
    ssoType: 'universal',
    apiBaseUrl: {
      staging: 'https://api.getvisitapp.net/venom',
      development: 'https://stage-tele-folloup.visithealth.co/backend',
    },
    pharmaServer: {
      development: 'https://api.getvisitapp.net/pharmacy',
      staging: 'https://api.getvisitapp.net/pharmacy',
    },
    useDopeRelatives: true,
    showSponsorLogoInWebVersion: false,
    showUpgradePackagesSection: false,
    showMyOrderInAhc: false,
    forceInAppCashlessCartRedirect: true,
    apiEncryptionKey: 'AFGEjMo+wBPagjeKFUtQi90ue9Wk95BuA9PoPP9Gd2Y=',
    ssoLandingScreen: 'teleconsult',
    hideTeleconsultBackButton: true,
    hideMyOrdersRelativeFilter: true,
    showBannerInEmptyConsultations: true,
    hidePrescriptionAddressNote: true,
    hideCallPreferenceIcons: true,

    theme: {
      colors: {
        primary: '#0065FF',
        secondary: '#253858',
        tertiary: '#FFAB00',
        shades1: '#B3D4FF',
        light: '#DEEBFF',
      },
      fonts: {
        primary: "'Roboto', system-ui, 'Segoe UI', Arial, sans-serif",
        secondary: "'Roboto', system-ui, 'Segoe UI', Arial, sans-serif",
      },
    },
  },
};

const clientConfig = clientConfigs[CLIENT] || clientConfigs.default;

// Theme resolved with fallback to the `default` profile, so a client entry can
// override just a subset of colors/fonts (or omit `theme` entirely).
const defaultTheme = clientConfigs.default.theme;
export const theme = {
  colors: { ...defaultTheme.colors, ...clientConfig.theme?.colors },
  fonts: { ...defaultTheme.fonts, ...clientConfig.theme?.fonts },
};

export const CLIENT_NAME = CLIENT;
export default clientConfig;
